import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { StatusBadge, StatusBadgeCard } from "@/components/ui/StatusBadge";
import { UrlChecker } from "@/components/UrlChecker";
import { Shield, Search, Database, Lock, ArrowRight, CheckCircle, Globe, AlertTriangle, Eye, Users, BookOpen, BadgeCheck } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Helmet } from "react-helmet-async";
import { supabase, type BlogPost } from "@/lib/supabase";

export default function Index() {
  const [latestPosts, setLatestPosts] = useState<BlogPost[]>([]);

  useEffect(() => {
    supabase
      .from("blogs")
      .select("slug, title, excerpt, created_at")
      .eq("published", true)
      .order("created_at", { ascending: false })
      .limit(3)
      .then(({ data }) => { if (data) setLatestPosts(data as BlogPost[]); });
  }, []);

  return (
    <Layout>
      <Helmet>
        <title>Is This Website Safe? Free Checker & Verified Badge</title>
        <meta name="description" content="Check if a website is safe, legit, or a scam — for free. Scans SSL, domain age, and threat databases. No signup required." />
        <meta property="og:title" content="Free Website Safety Checker — Is This Website Safe?" />
        <meta property="og:description" content="Instantly check any website for scams, phishing, and malware. Free tool powered by public threat intelligence." />
        <meta property="og:image" content="https://transparencybadge.com/og-image.png" />
        <meta property="og:url" content="https://transparencybadge.com/" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://transparencybadge.com/" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": "Transparency Badge Website Safety Checker",
          "url": "https://transparencybadge.com",
          "description": "Free website safety checker that scans any URL for scams, phishing, malware, and trust signals.",
          "applicationCategory": "SecurityApplication",
          "operatingSystem": "Web",
          "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" },
          "provider": {
            "@type": "Organization",
            "name": "Universal Website Transparency Badge",
            "url": "https://transparencybadge.com"
          }
        })}</script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative overflow-hidden border-b bg-gradient-to-b from-accent/50 to-background">
        <div className="container py-12 md:py-28">
          <div className="grid gap-8 lg:grid-cols-2 lg:gap-16 items-center">
            <div className="space-y-6 animate-fade-in">
              <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                Free Website Safety Tool — No Signup Needed
              </span>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-foreground text-balance leading-tight">
                Is This Website Safe? <span className="text-primary">Check Now — Free</span>
              </h1>
              <p className="text-base md:text-lg text-muted-foreground max-w-xl">
                Before you shop, sign up, or share your details — check the website first. Our free tool scans domain age, SSL certificates, threat databases, and server info to tell you if a site is safe or risky.
              </p>
              <p className="text-base md:text-lg lg:text-xl text-foreground bg-accent/40 border border-primary/20 rounded-lg px-4 py-3">
                <span className="font-bold text-foreground">For website owners:</span> Get a verified trust badge{" "}
                <span className="text-primary font-bold">(1 month free)</span>{" "}
                <span className="text-muted-foreground text-sm font-normal">— No card required</span>
              </p>
              <div className="flex flex-col sm:flex-row items-start gap-4 pt-2 pb-6 sm:pb-8">
                <Button asChild size="lg" className="btn-orange w-auto justify-center gap-2 px-5">
                  <a href="#check-url">
                    <Search className="h-4 w-4" />
                    <span>Check Any Website Free</span>
                    <ArrowRight className="h-4 w-4" />
                  </a>
                </Button>
                <Button asChild size="lg" className="btn-verified h-auto min-h-11 w-auto whitespace-nowrap justify-center gap-2 px-5 py-3 text-center">
                  <Link to="/get-verified">
                    <BadgeCheck className="h-4 w-4 shrink-0" />
                    <span>Get Your Website Verified</span>
                  </Link>
                </Button>
              </div>
            </div>

            <div className="flex justify-center lg:justify-end animate-fade-in-up" style={{ animationDelay: "0.2s" }}>
              <div className="relative w-full max-w-md">
                <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-safe/20 blur-3xl rounded-full" />
                <div className="relative bg-card rounded-2xl border shadow-elevated p-5 md:p-6">
                  <div className="flex items-center gap-6 mb-6">
                    <StatusBadge status="safe" size="lg" showLabel={false} />
                    <div>
                      <p className="text-sm text-muted-foreground">example-website.com</p>
                      <p className="text-lg font-semibold text-safe">Safe — Established Website</p>
                    </div>
                  </div>
                  <div className="space-y-3 pt-4 border-t">
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-safe" />
                      <span className="text-muted-foreground">No malware or phishing detected</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-safe" />
                      <span className="text-muted-foreground">Domain registered 5+ years ago</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-safe" />
                      <span className="text-muted-foreground">Valid SSL certificate active</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Check URL Section */}
      <section className="py-12 md:py-20 bg-gradient-to-b from-accent/30 to-background" id="check-url">
        <div className="container">
          <SectionHeader
            badge="Free Website Checker"
            title="Check If a Website Is Safe Right Now"
            description="Paste any URL below. We'll scan it against threat databases, check its SSL certificate, domain age, and server details — all in seconds. No account needed."
          />

          <div className="mt-10">
            <UrlChecker />
          </div>
        </div>
      </section>

      {/* Why Website Safety Matters */}
      <section className="py-12 md:py-20 bg-muted/30 border-y">
        <div className="container">
          <SectionHeader
            badge="Why It Matters"
            title="Why You Should Check a Website Before Trusting It"
            description="Scam websites look real. They use professional designs, HTTPS, and even fake reviews. Here's why checking first matters."
          />

          <div className="mt-8 md:mt-12 max-w-4xl mx-auto">
            <div className="grid gap-4 md:gap-6 md:grid-cols-2">
              <div className="bg-card rounded-xl border p-5 md:p-6 space-y-3 md:space-y-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-risk/10 text-risk">
                    <AlertTriangle className="h-5 w-5" />
                  </div>
                  <h3 className="font-semibold text-foreground">Online Scams Are Getting Smarter</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  Phishing websites, fake online stores, and credential-stealing pages cost people billions every year. Many of these sites look identical to real businesses — complete with logos, product images, and checkout pages. You can't tell by looking anymore. You need data.
                </p>
              </div>

              <div className="bg-card rounded-xl border p-5 md:p-6 space-y-3 md:space-y-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-safe/10 text-safe">
                    <Shield className="h-5 w-5" />
                  </div>
                  <h3 className="font-semibold text-foreground">Data Beats Guesswork</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  Instead of guessing, our tool checks real signals: Is the domain new or old? Is SSL valid? Has it been reported for malware or phishing? Does the server location match what the site claims? These facts help you decide — not gut feeling.
                </p>
              </div>

              <div className="bg-card rounded-xl border p-5 md:p-6 space-y-3 md:space-y-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <Globe className="h-5 w-5" />
                  </div>
                  <h3 className="font-semibold text-foreground">Scams Don't Respect Borders</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  A fake store targeting users in the US might be hosted in another continent. A phishing site impersonating an Indian bank could be registered in Eastern Europe. Our tool works globally — checking any website from anywhere, no matter what country you're in.
                </p>
              </div>

              <div className="bg-card rounded-xl border p-5 md:p-6 space-y-3 md:space-y-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <Users className="h-5 w-5" />
                  </div>
                  <h3 className="font-semibold text-foreground">Everyone Deserves to Know</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  You shouldn't need to be a cybersecurity expert to stay safe online. Whether you're a student, a parent, a freelancer, or a first-time online shopper — our tool gives you the same threat intelligence that security professionals use, in plain language.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Badge Examples */}
      <section className="py-12 md:py-20">
        <div className="container">
          <SectionHeader
            badge="What Results Look Like"
            title="Clear Results You Can Understand"
            description="After checking a website, you'll see one of four results. Each one tells you exactly what our scan found."
          />

          <div className="grid gap-4 md:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 mt-8 md:mt-12">
            <StatusBadgeCard
              status="safe"
              description="Domain is well-established, SSL is valid, and no threats were found in public databases. This is a good sign."
            />
            <StatusBadgeCard
              status="caution"
              description="The domain is new or has limited history. This doesn't mean it's dangerous — but be careful with personal info."
            />
            <StatusBadgeCard
              status="risk"
              description="Warning signals detected — the site may be flagged for phishing, malware, or suspicious activity. Avoid sharing data."
            />
            {/* Verified Badge — special card */}
            <div className="rounded-xl border-2 border-primary/40 p-5 md:p-6 bg-accent/30 transition-all hover:shadow-elevated h-full">
              <div className="flex items-start gap-4">
                <div className="rounded-full p-3 gradient-hero shrink-0">
                  <BadgeCheck className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1 text-primary">Verified by Transparency Badge</h3>
                  <p className="text-sm text-muted-foreground">The site owner applied, passed our eligibility check, and pays for ongoing automated monitoring to maintain verification. This is the highest trust level.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Preview */}
      <section className="py-12 md:py-20 bg-muted/30 border-y">
        <div className="container">
          <SectionHeader
            badge="How It Works"
            title="What Happens When You Check a Website"
            description="We don't just check one thing. We pull data from multiple sources and combine them into a single, easy-to-read result."
          />

          <div className="grid gap-4 md:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 mt-8 md:mt-12">
            {[
              {
                icon: <Shield className="h-6 w-6" />,
                title: "Threat Database Scan",
                description: "We check the domain against public threat intelligence feeds — including known phishing lists, malware databases, and abuse reports.",
              },
              {
                icon: <Search className="h-6 w-6" />,
                title: "Domain Age & History",
                description: "When was the domain registered? A site created yesterday is riskier than one that's been around for 5 years. We check registration records.",
              },
              {
                icon: <Lock className="h-6 w-6" />,
                title: "SSL & HTTPS Check",
                description: "We verify if the site uses HTTPS and whether its SSL certificate is valid. No encryption? That's a red flag.",
              },
              {
                icon: <Database className="h-6 w-6" />,
                title: "Combined Risk Score",
                description: "No single check decides the result. We weigh all signals together — threat data, domain age, SSL, and more — for a balanced assessment.",
              },
            ].map((item, index) => (
              <div
                key={index}
                className="bg-card rounded-xl border p-5 md:p-6 transition-all hover:shadow-elevated animate-fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-accent text-primary mb-4">
                  {item.icon}
                </div>
                <h3 className="font-semibold text-foreground mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.description}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <Button asChild variant="outline">
              <Link to="/how-it-works">
                See the Full Process
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* What You Can Check */}
      <section className="py-12 md:py-20">
        <div className="container">
          <SectionHeader
            badge="Use Cases"
            title="What Kind of Websites Can You Check?"
            description="Our tool works on any publicly accessible URL. Here are some of the most common reasons people use it."
          />

          <div className="mt-8 md:mt-12 max-w-4xl mx-auto">
            <div className="grid gap-4 md:gap-4 md:grid-cols-2">
              {[
                {
                  icon: <Eye className="h-5 w-5" />,
                  title: "Online Stores You've Never Heard Of",
                  description: "Found a great deal on an unfamiliar website? Check it before entering your credit card. Fake stores are one of the most common scams online.",
                },
                {
                  icon: <Globe className="h-5 w-5" />,
                  title: "Links from Emails or Social Media",
                  description: "Got a link via WhatsApp, email, or DM? Paste it into our checker before clicking. Phishing links often look normal but lead to dangerous sites.",
                },
                {
                  icon: <Lock className="h-5 w-5" />,
                  title: "Banking & Financial Websites",
                  description: "Make sure you're on the real banking site — not a phishing clone. Our tool checks whether the domain is established and has valid security.",
                },
                {
                  icon: <BookOpen className="h-5 w-5" />,
                  title: "Online Courses & Educational Platforms",
                  description: "Before paying for a course or sharing your details, verify the platform is legitimate. New doesn't mean fake, but a quick check gives you peace of mind.",
                },
                {
                  icon: <Users className="h-5 w-5" />,
                  title: "Freelance & Job Platforms",
                  description: "Remote job scams are on the rise. If someone sends you a link to apply or sign up, check the website first. It takes 5 seconds.",
                },
                {
                  icon: <Shield className="h-5 w-5" />,
                  title: "Any Website That Asks for Your Data",
                  description: "If a website is asking for your name, email, phone, payment info, or address — check it first. A legitimate site won't mind you verifying.",
                },
              ].map((item, index) => (
                <div key={index} className="bg-card rounded-xl border p-4 md:p-5 flex items-start gap-3 md:gap-4 transition-all hover:shadow-elevated">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent text-primary">
                    {item.icon}
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">{item.title}</h3>
                    <p className="text-sm text-muted-foreground">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Trust Statistics */}
      <section className="py-12 md:py-20 bg-muted/30 border-y">
        <div className="container">
          <SectionHeader
            badge="The Problem"
            title="Online Scams in Numbers"
            description="These aren't scare tactics — they're real. Here's why checking websites matters more than ever."
          />

          <div className="mt-8 md:mt-12 grid gap-4 md:gap-6 md:grid-cols-3 max-w-4xl mx-auto">
            <div className="bg-card rounded-xl border p-5 md:p-6 text-center">
              <p className="text-2xl md:text-3xl font-bold text-primary mb-2">3.4 Billion</p>
              <p className="text-sm text-muted-foreground">Phishing emails sent every single day. Many link to fake websites built to steal your credentials or money.</p>
            </div>
            <div className="bg-card rounded-xl border p-5 md:p-6 text-center">
              <p className="text-2xl md:text-3xl font-bold text-primary mb-2">$10 Billion+</p>
              <p className="text-sm text-muted-foreground">Lost to online scams globally each year. Individuals, small businesses, and even large companies are affected.</p>
            </div>
            <div className="bg-card rounded-xl border p-5 md:p-6 text-center">
              <p className="text-2xl md:text-3xl font-bold text-primary mb-2">1.5 Million</p>
              <p className="text-sm text-muted-foreground">New phishing websites created every month. Most stay active for just hours — long enough to steal data before vanishing.</p>
            </div>
          </div>

          <div className="mt-8 text-center">
            <p className="text-sm text-muted-foreground max-w-2xl mx-auto">
              A single check takes seconds. It could save you from identity theft, financial loss, or malware infection. Why risk it?
            </p>
          </div>
        </div>
      </section>

      {/* For Website Owners */}
      <section className="py-12 md:py-20">
        <div className="container">
          <SectionHeader
            badge="For Website Owners"
            title="Own a Website? Show Visitors They Can Trust You"
            description="New websites get flagged as 'Unknown' — even if they're completely legitimate. The Verified Badge fixes that."
          />

          <div className="mt-8 md:mt-12 max-w-3xl mx-auto">
            <div className="bg-card border-2 border-primary/30 rounded-2xl p-5 sm:p-8 md:p-10 space-y-5 md:space-y-6">
              <div className="flex items-center gap-3 md:gap-4">
                <div className="w-12 h-12 md:w-14 md:h-14 rounded-full gradient-hero flex items-center justify-center shrink-0">
                  <BadgeCheck className="h-6 w-6 md:h-7 md:w-7 text-white" />
                </div>
                <div>
                  <h3 className="text-lg md:text-xl font-bold text-foreground">Verified Badge Program</h3>
                  <p className="text-xs md:text-sm text-muted-foreground">Eligibility check · Continuous monitoring · $10/month · First month free</p>
                </div>
              </div>
              <p className="text-muted-foreground">
                When someone checks your website using our tool, they'll see "Verified by Transparency Badge" instead of "New / Unknown." You also get an embeddable trust badge for your site that visitors can click to confirm your verification.
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                {[
                  "One-time eligibility check against published criteria",
                  "Checker result changes to 'Verified'",
                  "Embeddable badge HTML for your website",
                  "Continuous monitoring while active",
                  "Badge revoked if safety standards violated",
                  "Cancel anytime — no contracts",
                ].map((f) => (
                  <div key={f} className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-safe shrink-0 mt-0.5" />
                    <span className="text-sm text-foreground">{f}</span>
                  </div>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <Button
                  asChild
                  size="lg"
                  className="btn-verified h-auto min-h-11 w-full whitespace-normal px-4 py-3 text-center leading-snug sm:w-auto sm:whitespace-nowrap"
                >
                  <Link to="/get-verified">
<span className="sm:hidden">Get Website Verified — Free</span>
                    <span className="hidden sm:inline">Get Your Website Verified — 1 Month Free</span>
                    <ArrowRight className="hidden h-4 w-4 sm:block" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="h-auto min-h-11 w-full px-4 py-3 text-center sm:w-auto">
                  <Link to="/pricing">See Pricing Details</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Highlights */}
      <section className="py-12 md:py-20 bg-muted/30 border-y">
        <div className="container">
          <SectionHeader
            badge="Learn More"
            title="Guides to Staying Safe Online"
            description="Practical articles about spotting scams, checking websites, and protecting your personal information."
          />

          <div className="mt-8 md:mt-12 grid gap-4 md:gap-6 md:grid-cols-3 max-w-5xl mx-auto">
            {latestPosts.length === 0
              ? [1, 2, 3].map((i) => (
                  <div key={i} className="bg-card rounded-xl border p-5 md:p-6 animate-pulse">
                    <div className="h-4 w-3/4 rounded bg-muted mb-3" />
                    <div className="h-3 w-full rounded bg-muted mb-2" />
                    <div className="h-3 w-5/6 rounded bg-muted mb-2" />
                    <div className="h-3 w-2/3 rounded bg-muted" />
                  </div>
                ))
              : latestPosts.map((post) => (
                  <Link
                    key={post.slug}
                    to={`/blog/${post.slug}`}
                    className="bg-card rounded-xl border p-5 md:p-6 transition-all hover:shadow-elevated group"
                  >
                    <h3 className="font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">{post.title}</h3>
                    <p className="text-sm text-muted-foreground mb-4 line-clamp-3">{post.excerpt}</p>
                    <span className="text-sm font-medium text-primary inline-flex items-center gap-1">
                      Read the full guide <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-1" />
                    </span>
                  </Link>
                ))
            }
          </div>

          <div className="text-center mt-10">
            <Button asChild variant="outline">
              <Link to="/blog">
                All Articles
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 md:py-20">
        <div className="container">
          <SectionHeader
            badge="FAQ"
            title="Common Questions"
            description="Quick answers about how our website safety checker works and what results mean."
          />

          <div className="mt-8 md:mt-12 max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="item-1">
                <AccordionTrigger>Is the website safety checker really free?</AccordionTrigger>
                <AccordionContent>
                  Yes, 100% free. No registration, no limits, no hidden costs. You can check as many websites as you want. We built this as a public safety tool — anyone should be able to verify a website before trusting it.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-2">
                <AccordionTrigger>How does the website check actually work?</AccordionTrigger>
                <AccordionContent>
                  When you enter a URL, we query multiple public data sources: threat intelligence feeds (to check for known phishing or malware), domain registration records (to check how old the domain is), and SSL certificate databases (to verify encryption). We combine all of these signals into a single result — green, yellow, red, or verified.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-3">
                <AccordionTrigger>What do the badge colors mean?</AccordionTrigger>
                <AccordionContent>
                  <strong>Green (Established)</strong> — the domain is old, has valid SSL, and no threats were found. Automatic check only. <strong>Yellow (New/Unknown)</strong> — limited data available, possibly a new domain. Not necessarily dangerous. <strong>Red (Risk)</strong> — warning signals detected, such as phishing reports or malware flags. <strong>Teal (Verified)</strong> — the site owner applied, passed our eligibility check, and pays for ongoing monitoring. This is the highest trust level.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-4">
                <AccordionTrigger>Does a green result guarantee a website is safe?</AccordionTrigger>
                <AccordionContent>
                  No. A green result means no threats were found at the time of checking and the domain is well-established. But no tool can guarantee 100% safety. Website status can change — a safe site today could be compromised tomorrow. Always use your own judgment, especially before sharing payment details or personal data.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-5">
                <AccordionTrigger>Do you collect my personal data when I use the checker?</AccordionTrigger>
                <AccordionContent>
                  No. We process only the domain name you enter. We don't collect your name, email, IP address, or browsing history. No account is needed. No cookies are used for tracking. The checker is completely anonymous.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-6">
                <AccordionTrigger>What is the Verified Badge and who is it for?</AccordionTrigger>
                <AccordionContent>
                  The Verified Badge is for website owners who want to prove their site is legitimate. When you apply, your site goes through a one-time eligibility check against our published criteria. If approved, your domain shows as "Verified by Transparency Badge" when anyone checks it, your site is monitored continuously for as long as you subscribe, and you get an embeddable trust badge for your site. It costs $10/month per domain, with the first month free. It's especially useful for new online stores, SaaS products, and freelancer websites that need to build trust with visitors.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-7">
                <AccordionTrigger>Can I check any website in the world?</AccordionTrigger>
                <AccordionContent>
                  Yes. Our tool works on any publicly accessible domain — regardless of country, language, or industry. Whether it's an online store in the US, a banking site in India, or a freelance platform in Europe — just paste the URL and check.
                </AccordionContent>
              </AccordionItem>

            </Accordion>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-12 md:py-20 border-t">
        <div className="container text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
            Don't Trust a Website Until You've Checked It
          </h2>
          <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto mb-6 md:mb-8">
            It takes 5 seconds to check a website. It can take months to recover from a scam. Use our free tool to check any URL right now — no signup, no cost, no catch.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-3 md:gap-4">
            <Button asChild size="lg" className="btn-orange">
              <a href="#check-url">
                <Search className="mr-2 h-4 w-4" />
                Check a Website Now — It's Free
                <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </Button>
<Button asChild size="lg" className="btn-verified h-auto min-h-11 w-full sm:w-auto whitespace-normal sm:whitespace-nowrap px-4 py-3 text-center">
              <Link to="/get-verified">
                <BadgeCheck className="mr-2 h-4 w-4 shrink-0" />
                <span className="sm:hidden">Get Website Verified — Free</span>
                <span className="hidden sm:inline">Get Your Website Verified</span>
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
}
